/* Shared homepage/collection review navigation, revision curated-2026-09-13.
   Mobile: one active card in normal flow; optional full-text expansion.
   Desktop: preserve the existing native-scroll, multi-card presentation.
   Prominent homepage/collection placements use an explicit Judge.me review-ID whitelist.
   Source review text, ratings and Judge.me records are not rewritten or deleted. */
(() => {
  const states = new WeakMap();
  const observed = new WeakSet();
  const mobile = window.matchMedia('(max-width: 767px)');
  let sequence = 0;
  const setText = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const approvedFeaturedReviewIds = new Set([
    'a78b84ae-b83f-42cc-8800-dd36740e2b86',
    '5b03a296-8d5c-4608-93f1-a5796e6109a1',
    '9564d095-9509-46a7-93c4-eaa678fa7f41',
    'd7df1b2d-2e21-4554-8478-5067bae8efa3',
    'bff26290-e277-4dc2-b1b1-945952cebfe0',
    '3a690275-cc00-4069-96de-d637962d0834',
    '71f6fb23-519c-416b-880b-bda5a20766ec',
    'f8ac8590-70b8-4c4b-8498-53e97210eda9'
  ]);

  function init(carousel) {
    const viewport = carousel.querySelector('.jdgm-carousel__item-container');
    const track = viewport && viewport.querySelector('.jdgm-carousel__item-wrapper');
    if (!viewport || !track) return;

    const sourceCards = Array.from(track.children).filter((node) => node.classList.contains('jdgm-carousel-item'));
    const hasReviewIds = sourceCards.some((node) => node.dataset.reviewId);
    if (hasReviewIds) {
      sourceCards.forEach((node) => {
        if (!approvedFeaturedReviewIds.has(node.dataset.reviewId || '')) node.remove();
      });
    }

    const cards = Array.from(track.children).filter((node) => node.classList.contains('jdgm-carousel-item'));
    if (!cards.length) return;
    const existing = states.get(carousel);
    if (existing && existing.viewport === viewport && existing.track === track) {
      existing.cards = cards;
      existing.refresh();
      return;
    }
    if (existing) existing.destroy();

    const cleanup = [];
    const listen = (node, event, handler, options) => {
      node.addEventListener(event, handler, options);
      cleanup.push(() => node.removeEventListener(event, handler, options));
    };
    const state = { viewport, track, cards, index: 0, mode: '', queued: false };
    states.set(carousel, state);
    viewport.dataset.layoutNavReady = 'true';
    viewport.tabIndex = 0;
    viewport.setAttribute('role', 'region');
    viewport.id = viewport.id || `ReviewViewport-${++sequence}`;

    const oldControls = carousel.querySelector('.verified-reviews__controls');
    if (oldControls) oldControls.remove();
    const controls = document.createElement('div');
    controls.className = 'verified-reviews__controls';
    controls.setAttribute('aria-label', 'Review navigation');
    const previous = document.createElement('button');
    const next = document.createElement('button');
    [previous, next].forEach((button) => {
      button.type = 'button';
      button.className = 'verified-reviews__control';
      button.setAttribute('aria-controls', viewport.id);
      controls.appendChild(button);
    });
    previous.textContent = 'Previous reviews';
    next.textContent = 'Next reviews';
    const position = document.createElement('p');
    position.className = 'review-mobile-position';
    position.setAttribute('aria-live', 'polite');
    position.setAttribute('aria-atomic', 'true');
    controls.appendChild(position);
    const widget = viewport.closest('.jdgm-carousel');
    carousel.insertBefore(controls, widget && widget.parentElement === carousel ? widget : carousel.firstChild);

    function prepareBody(card) {
      const body = card.querySelector('.jdgm-carousel-item__review-body');
      if (!body) return;
      let button = card.querySelector('.review-mobile-expand');
      if (!button) {
        body.id = body.id || `ReviewBody-${++sequence}`;
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'review-mobile-expand';
        button.setAttribute('aria-controls', body.id);
        button.setAttribute('aria-expanded', 'false');
        button.textContent = 'Read full review';
        button.hidden = true;
        body.insertAdjacentElement('afterend', button);
      }
      const open = body.dataset.mobileExpanded === 'true';
      if (!open) body.dataset.mobileCollapsible = 'true';
      const isLong = body.scrollHeight > body.clientHeight + 2;
      const showButton = open || isLong;
      if (!showButton) body.removeAttribute('data-mobile-collapsible');
      button.hidden = !showButton;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      setText(button, open ? 'Show less' : 'Read full review');
    }

    function sync() {
      if (state.destroyed) return;
      if (!carousel.isConnected) { state.destroy(); return; }
      const nextMode = mobile.matches ? 'mobile' : 'desktop';
      if (state.mode !== nextMode) {
        if (nextMode === 'mobile') {
          const width = state.cards[0].getBoundingClientRect().width;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          if (width + gap > 0) state.index = Math.round(viewport.scrollLeft / (width + gap));
        }
        state.mode = nextMode;
        carousel.dataset.reviewLayout = nextMode;
        viewport.scrollLeft = 0;
      }
      state.index = Math.max(0, Math.min(state.index, state.cards.length - 1));
      state.cards.forEach((card, index) => {
        const active = nextMode === 'mobile' && index === state.index;
        card.classList.toggle('is-mobile-current', active);
        if (nextMode === 'mobile') card.setAttribute('aria-hidden', active ? 'false' : 'true');
        else card.removeAttribute('aria-hidden');
      });
      let showControls;
      if (nextMode === 'mobile') {
        viewport.setAttribute('aria-label', 'Customer reviews. Swipe or use the previous and next buttons.');
        prepareBody(state.cards[state.index]);
        setText(position, `Review ${state.index + 1} of ${state.cards.length}`);
        previous.disabled = state.index === 0;
        next.disabled = state.index === state.cards.length - 1;
        showControls = state.cards.length > 1;
      } else {
        viewport.setAttribute('aria-label', 'Customer reviews. Scroll to read more reviews.');
        const limit = viewport.scrollWidth - viewport.clientWidth;
        previous.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= limit - 2;
        showControls = limit > 2;
      }
      controls.hidden = !showControls;
      if (showControls) controls.style.removeProperty('display');
      else controls.style.display = 'none';
    }
    function schedule() {
      if (state.queued || state.destroyed) return;
      state.queued = true;
      requestAnimationFrame(() => { state.queued = false; sync(); });
    }
    function move(direction) {
      if (mobile.matches) {
        state.index = Math.max(0, Math.min(state.index + direction, state.cards.length - 1));
        sync();
      } else {
        const card = state.cards[0];
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = card.getBoundingClientRect().width + gap;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        viewport.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
    listen(previous, 'click', () => move(-1));
    listen(next, 'click', () => move(1));
    listen(viewport, 'scroll', schedule, { passive: true });
    listen(viewport, 'keydown', (event) => {
      if (event.target !== viewport || !mobile.matches) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      }
    });
    let touchStart = null;
    listen(viewport, 'touchstart', (event) => {
      touchStart = mobile.matches && event.touches.length === 1
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    listen(viewport, 'touchend', (event) => {
      if (!touchStart || !mobile.matches || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - touchStart.x;
      const dy = event.changedTouches[0].clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) move(dx < 0 ? 1 : -1);
    }, { passive: true });
    listen(viewport, 'touchcancel', () => { touchStart = null; }, { passive: true });
    listen(carousel, 'click', (event) => {
      const button = event.target.closest('.review-mobile-expand');
      if (!button || !mobile.matches) return;
      const card = button.closest('.jdgm-carousel-item');
      const body = card && card.querySelector('.jdgm-carousel-item__review-body');
      if (!body) return;
      body.dataset.mobileExpanded = body.dataset.mobileExpanded === 'true' ? 'false' : 'true';
      sync();
    });
    if (mobile.addEventListener) listen(mobile, 'change', schedule);
    else { mobile.addListener(schedule); cleanup.push(() => mobile.removeListener(schedule)); }
    listen(window, 'resize', schedule, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      observer.observe(viewport);
      cleanup.push(() => observer.disconnect());
    }
    state.refresh = schedule;
    state.destroy = () => { state.destroyed = true; cleanup.forEach((fn) => fn()); states.delete(carousel); };
    sync();
  }

  function setup(scope = document) {
    scope.querySelectorAll('.verified-reviews__carousel').forEach(init);
    scope.querySelectorAll('.verified-reviews__widget').forEach((widget) => {
      if (observed.has(widget)) return;
      observed.add(widget);
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          widget.querySelectorAll('.verified-reviews__carousel').forEach(init);
        });
      }).observe(widget, { childList: true, subtree: true });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup(), { once: true });
  else setup();
  window.addEventListener('load', () => setup(), { once: true });
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
